import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const BANNER_IMAGE =
  'https://roadtechservices.netlify.app/images/projects_banner-850w.jpeg';
const ABOUT_IMAGE =
  'https://roadtechservices.netlify.app/images/mower-repair-3-850w.jpeg';
const CTA_IMAGE =
  'https://roadtechservices.netlify.app/images/mower_banner-850w.jpeg';

const certs = [
  {
    brand: 'Briggs & Stratton',
    level: 'Master Certified',
    icon: 'ri-star-fill',
    description:
      'The highest technician certification level — covering comprehensive small engine diagnosis, repair, and overhaul.',
  },
  {
    brand: 'Stihl',
    level: 'Silver Level Certified',
    icon: 'ri-award-fill',
    description:
      'Professional certification for outdoor power equipment service and maintenance.',
  },
  {
    brand: 'Kohler',
    level: 'Certified Technician',
    icon: 'ri-shield-fill',
    description:
      'Trained and certified for Kohler engine systems, ensuring accurate diagnosis and quality repair.',
  },
  {
    brand: 'Kawasaki',
    level: 'Certified Technician',
    icon: 'ri-verified-badge-fill',
    description:
      'Certified to service and repair Kawasaki-powered outdoor equipment with manufacturer-approved techniques.',
  },
];

const faqs = [
  {
    q: 'What equipment do you repair?',
    a: 'We provide mobile repair for lawn mowers (push and riding), snow blowers (single and two-stage), and generators. If you\'re unsure, give us a call and we\'ll let you know if we can help.',
  },
  {
    q: 'Do you offer same-day service?',
    a: "Yes! In many cases, repairs can be completed the same day, provided the issue is described accurately and the necessary parts are available.",
  },
  {
    q: 'Do I need to bring my equipment to you?',
    a: "No — we come to you. Road Tech Services is a fully mobile repair service, so all work is completed at your home for maximum convenience.",
  },
  {
    q: 'What certifications do you have?',
    a: "Our technician is Master Certified with Briggs & Stratton, Silver-Level Certified with Stihl, and also certified with Kohler and Kawasaki — ensuring expert repair every time.",
  },
  {
    q: 'Why choose Road Tech Services?',
    a: "We make repair simple, fast, and stress-free. With professional certifications, mobile convenience, and reliable same-day service, Road Tech Services is the smart choice for keeping your equipment in top condition.",
  },
];

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="font-body">
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-40 pb-24 flex items-center justify-center min-h-[420px]">
        <div className="absolute inset-0">
          <img
            src={BANNER_IMAGE}
            alt="About Road Tech Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative z-10 text-center px-6">
          <p className="text-orange-400 text-xs font-bold uppercase tracking-widest mb-3">
            Who We Are
          </p>
          <h1 className="font-display text-5xl lg:text-7xl font-bold text-white uppercase tracking-tight">
            About Us
          </h1>
        </div>
      </section>

      {/* About Content */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="h-[520px] rounded-2xl overflow-hidden">
                <img
                  src={ABOUT_IMAGE}
                  alt="Road Tech Services mobile repair van and equipment"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 right-4 bg-white border border-stone-200 p-5 rounded-xl">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-9 h-9 flex items-center justify-center bg-orange-100 rounded-lg">
                    <i className="ri-map-pin-2-line text-orange-600 text-base" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">
                      Serving Michigan
                    </div>
                    <div className="text-neutral-500 text-xs">
                      We come to your home
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-4">
                About Us
              </p>
              <h2 className="font-display text-4xl lg:text-5xl font-bold uppercase tracking-tight text-neutral-900 mb-6 leading-tight">
                About Road Tech Services
              </h2>
              <p className="text-neutral-600 text-base leading-relaxed mb-5">
                Road Tech Services is a locally owned mobile repair business
                proudly serving homeowners across Michigan. We specialize in
                on-site repair and maintenance for lawn mowers, snow blowers,
                and generators — saving customers the hassle of transporting
                heavy equipment to a shop.
              </p>
              <p className="text-neutral-600 text-base leading-relaxed mb-8">
                Our mission is simple: make small engine repair convenient,
                professional, and reliable. With expert certifications and
                same-day service available, we ensure your equipment is back up
                and running quickly — right in your driveway.
              </p>

              {/* Matt Quote */}
              <div className="bg-stone-50 border-l-4 border-orange-500 pl-6 py-5 rounded-r-xl mb-8">
                <p className="text-neutral-700 text-sm leading-relaxed italic mb-4">
                  &ldquo;I&rsquo;m Matt Olenzek, founder of Road Tech Services.
                  As a Master Certified Technician for Briggs &amp; Stratton,
                  Silver-Level Certified in Stihl, and also certified in Kohler
                  and Kawasaki, I&rsquo;ve built this business to give
                  homeowners an easier way to keep their outdoor equipment in
                  peak condition. My goal is to deliver professional, same-day
                  repair with honesty, care, and attention to detail.&rdquo;
                </p>
                <div>
                  <span className="font-semibold text-neutral-900 text-sm block">
                    Matt Olenzek
                  </span>
                  <span className="text-neutral-500 text-xs">
                    Owner &amp; Master Certified Technician
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  Book a Service
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 bg-white border border-stone-200 hover:border-stone-300 text-neutral-800 font-semibold px-7 py-3.5 rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  View Services
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="bg-stone-50 py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">
              Credentials
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold uppercase tracking-tight text-neutral-900">
              Certified Across Top Brands
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {certs.map((cert) => (
              <div
                key={cert.brand}
                className="bg-white rounded-xl p-6 border border-stone-200 hover:border-orange-200 transition-colors"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-orange-100 rounded-xl mb-4">
                  <i className={`${cert.icon} text-orange-600 text-lg`} />
                </div>
                <div className="text-orange-600 text-xs font-semibold uppercase tracking-wider mb-1">
                  {cert.level}
                </div>
                <div className="font-display text-xl font-bold text-neutral-900 uppercase tracking-tight mb-3">
                  {cert.brand}
                </div>
                <p className="text-neutral-500 text-xs leading-relaxed">
                  {cert.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">
              FAQs
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold uppercase tracking-tight text-neutral-900">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-stone-200 rounded-xl overflow-hidden"
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-neutral-900 text-sm pr-4">
                    {faq.q}
                  </span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`${
                        openFaq === i ? 'ri-subtract-line' : 'ri-add-line'
                      } text-orange-600 text-lg`}
                    />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-neutral-600 text-sm leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32">
        <div className="absolute inset-0">
          <img
            src={CTA_IMAGE}
            alt="Mobile repair service"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-5">
            Keep Your Equipment Running
          </h2>
          <p className="text-white/75 text-base leading-relaxed mb-8">
            Don&rsquo;t waste time hauling your mower, snow blower, or
            generator to a shop. Contact Road Tech Services for professional,
            same-day mobile repair.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
          >
            Book a Service
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
